import { useMutation, useQueryClient } from '@tanstack/react-query';
import { advanceApplicationsQueryKey, availableAdvancesQueryKey } from './keys';
import { normalizeAdvanceApplication } from '../types';
import type {
  AdvancesHttpRequest,
  CreateAdvanceApplicationRequest,
  DocumentAdvanceApplicationDto,
  RawDocumentAdvanceApplicationDto,
} from '../types';

/** Contador de ids optimistas (estable entre renders, único por sesión). */
let optimisticCounter = 0;

export interface UseApplyAdvanceParams {
  documentId: string;
  /** Endpoint POST de reserva (`/documents/{id}/advances`). */
  applyAdvanceEndpoint: string;
  httpRequest: AdvancesHttpRequest;
}

export interface ApplyAdvanceInput {
  openBalanceId: string;
  appliedAmount: number;
  /** Moneda del documento, para la fila optimista (opcional). */
  currencyId?: string;
  /** Referencia del saldo origen, para la fila optimista (opcional). */
  sourceDocumentReference?: string | null;
}

interface ApplyMutationContext {
  previous?: DocumentAdvanceApplicationDto[];
}

/**
 * Reserva un anticipo (`POST /{id}/advances`) con optimistic update.
 *
 * - `onMutate`: agrega una fila `RESERVED` temporal al cache de aplicaciones.
 * - `onError`: hace rollback al snapshot previo (CA-F5).
 * - `onSettled`: invalida `['advance-applications']` y `['available-advances']` (CA-F7).
 */
export function useApplyAdvance({
  documentId,
  applyAdvanceEndpoint,
  httpRequest,
}: UseApplyAdvanceParams) {
  const queryClient = useQueryClient();
  const appsKey = advanceApplicationsQueryKey(documentId);
  const availKey = availableAdvancesQueryKey(documentId);

  return useMutation<DocumentAdvanceApplicationDto, Error, ApplyAdvanceInput, ApplyMutationContext>({
    mutationFn: async (input) => {
      const payload: CreateAdvanceApplicationRequest = {
        openBalanceId: input.openBalanceId,
        appliedAmount: input.appliedAmount,
      };
      const raw = await httpRequest<RawDocumentAdvanceApplicationDto>(
        'POST',
        applyAdvanceEndpoint,
        payload,
      );
      return normalizeAdvanceApplication(raw);
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: appsKey });
      const previous = queryClient.getQueryData<DocumentAdvanceApplicationDto[]>(appsKey);

      const optimistic: DocumentAdvanceApplicationDto = {
        id: `optimistic-${optimisticCounter++}`,
        documentId,
        openBalanceId: input.openBalanceId,
        appliedAmount: input.appliedAmount,
        currencyId: input.currencyId ?? '',
        status: 'RESERVED',
        sourceDocumentReference: input.sourceDocumentReference ?? null,
        reservedAt: new Date().toISOString(),
        rowVersion: 0,
      };

      queryClient.setQueryData<DocumentAdvanceApplicationDto[]>(appsKey, (prev) => [
        ...(prev ?? []),
        optimistic,
      ]);

      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(appsKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: appsKey });
      queryClient.invalidateQueries({ queryKey: availKey });
    },
  });
}
